/**
 * Pure display helpers for product rows (GetProducts shape).
 * Mirrors ProductCardGrid formatting so the insert page shows the same values.
 */

export function formatProductMoneyInteger(value) {
  const n = Math.round(Number(value));
  if (Number.isNaN(n)) return null;
  return n.toLocaleString();
}

export function productPremiumNumber(row) {
  if (!row) return null;
  if (row.premiumAmount != null && !Number.isNaN(Number(row.premiumAmount))) {
    return Math.round(Number(row.premiumAmount));
  }
  if (row.premium != null && !Number.isNaN(Number(row.premium))) {
    return Math.round(Number(row.premium));
  }
  const raw = row.premiumDisplay;
  if (raw != null && String(raw).trim() !== "") {
    const n = parseFloat(String(raw).replace(/\s/g, "").replace(",", "."));
    if (!Number.isNaN(n)) return Math.round(n);
  }
  return null;
}

export function productSumInsuredNumber(row) {
  if (!row) return null;
  if (row.sumInsured != null && !Number.isNaN(Number(row.sumInsured))) {
    return Number(row.sumInsured);
  }
  if (row.sumInsuredAmount != null && !Number.isNaN(Number(row.sumInsuredAmount))) {
    return Number(row.sumInsuredAmount);
  }
  const s = row.sumInsuredDisplay;
  if (s == null || !String(s).trim()) return null;
  const digits = String(s).replace(/\D/g, "");
  if (!digits) return null;
  const parsed = parseInt(digits, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

/** IsStatic: premium is fixed for the whole contract, not multiplied by insured count. */
export function productIsStatic(row) {
  const raw = row?.IsStatic ?? row?.isStatic ?? row?.is_static;
  if (raw == null) return false;
  if (typeof raw === "boolean") return raw;
  const s = String(raw).trim().toLowerCase();
  return s === "true" || s === "1";
}

/** IsStatic: fixed for whole contract; otherwise unit amount × insured count. */
export function resolveProductAmountByInsuredCount(product, unitAmount, insuredCount) {
  if (unitAmount == null || Number.isNaN(Number(unitAmount))) return null;
  const unit = Number(unitAmount);
  if (productIsStatic(product)) return unit;
  const count = Number(insuredCount);
  if (!Number.isFinite(count) || count <= 0) return null;
  return unit * count;
}

export function resolvePremiumCalculated(product, insuredCount) {
  return resolveProductAmountByInsuredCount(product, productPremiumNumber(product), insuredCount);
}

export function resolveSumInsuredCalculated(product, insuredCount) {
  return resolveProductAmountByInsuredCount(product, productSumInsuredNumber(product), insuredCount);
}

export function productPeriodLabel(row) {
  const raw = row?.period ?? row?.periodMonths;
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  return String(Math.round(n));
}

/** İnzibati xərclər — numeric value for API (deductible). */
export function productAdminChargeNumber(row) {
  const raw =
    row?.adminCharge ?? row?.AdminCharge ?? row?.adminChargeAmount ?? row?.AdminChargeAmount;
  if (raw == null || raw === "") return null;
  const str = String(raw).trim().replace("%", "").replace(",", ".");
  const n = Number(str);
  return Number.isFinite(n) ? n : null;
}

/** İnzibati xərclər — always shown as percent (never currency). */
export function productAdminChargeLabel(row) {
  const n = productAdminChargeNumber(row);
  if (n == null) return null;
  return `${n}%`;
}

export function productTextField(row, ...keys) {
  if (!row) return null;
  for (const key of keys) {
    const v = row[key];
    if (v != null && String(v).trim() !== "") return String(v).trim();
  }
  return null;
}

function normalizeNameEntry(s) {
  if (s == null) return "";
  let t = String(s).trim();
  if ((t.startsWith('"') && t.endsWith('"')) || (t.startsWith("'") && t.endsWith("'"))) {
    t = t.slice(1, -1).trim();
  }
  return t;
}

function resolveNameItem(item) {
  if (item == null) return "";
  if (typeof item === "string" || typeof item === "number" || typeof item === "boolean") {
    return normalizeNameEntry(item);
  }
  if (typeof item === "object") {
    const fields = [
      item.serviceName,
      item.ServiceName,
      item.exceptName,
      item.ExceptName,
      item.name,
      item.Name,
      item.title,
      item.Title,
      item.text,
      item.label,
      item.value,
      item.description,
    ];
    for (const f of fields) {
      const t = normalizeNameEntry(f);
      if (t) return t;
    }
    const strings = Object.values(item).filter(
      (v) => typeof v === "string" && String(v).trim()
    );
    if (strings.length === 1) return normalizeNameEntry(strings[0]);
  }
  return "";
}

function pushNameSegments(out, raw) {
  const seg = resolveNameItem(raw);
  if (!seg) return;
  if (/[,;|]/.test(seg)) {
    out.push(...seg.split(/[,;|]/u).map((s) => s.trim()).filter(Boolean));
  } else {
    out.push(seg);
  }
}

function coerceNameSource(v) {
  if (v == null) return null;
  if (Array.isArray(v)) return v;
  if (typeof v === "string") {
    const t = v.trim();
    if (!t) return null;
    if (t.startsWith("[") || t.startsWith("{")) {
      try {
        const parsed = JSON.parse(t);
        if (Array.isArray(parsed)) return parsed;
        if (parsed && typeof parsed === "object") return Object.values(parsed);
      } catch {
        /* plain string */
      }
    }
    return [t];
  }
  if (typeof v === "object") {
    if (Array.isArray(v.items)) return v.items;
    if (Array.isArray(v.data)) return v.data;
    return Object.values(v);
  }
  return null;
}

function nameList(row, ...keys) {
  const out = [];
  if (!row) return out;
  for (const key of keys) {
    const source = coerceNameSource(row[key]);
    if (!source) continue;
    for (const item of source) {
      pushNameSegments(out, item);
    }
  }
  return out;
}

export function productServiceList(row) {
  const fromApi = nameList(row, "serviceNames", "ServiceNames");
  if (fromApi.length) return fromApi;
  return nameList(row, "includedServices");
}

export function productExceptList(row) {
  const fromApi = nameList(row, "exceptNames", "ExceptNames");
  if (fromApi.length) return fromApi;
  const inel = row?.ineligibility;
  if (typeof inel === "string" && inel.trim()) {
    const t = inel.trim();
    if (/^\d+$/.test(t)) return [];
    const parts = t.split(/[.;]/u).map((s) => s.trim()).filter(Boolean);
    if (parts.length > 1) return parts;
    return [t];
  }
  return [];
}
